import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faCheck,
    faChevronDown,
    faChevronRight,
    faCopy,
    faKey,
    faLink,
    faPen,
    faShieldHalved,
    faSquarePlus,
    faSquareXmark,
    faUnlink,
} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {forkJoin, Subscription} from 'rxjs';
import {validateAllFormFields} from '../../../../common/utils';
import {AuthService} from '../../../services/auth.service';
import {ApiService} from '../../../services/api.service';
import {CopyButtonComponent} from '../../../shared/copy-button/copy-button.component';
import {
    OperationalWarningCardComponent,
} from '../../../shared/operational-readiness/operational-warning-card/operational-warning-card.component';
import {OperationalWarningView} from '../../../shared/operational-readiness/operational-readiness.models';
import {VtePaginationComponent} from '../../../shared/vte/vte-pagination/vte-pagination.component';
import {
    CustomerIntegrationActivityItem,
    CustomerIntegrationApiKey,
    CustomerIntegrationChecklistItem,
    CustomerWebhookIntegration,
    DeliveryHistoryFilters,
    DeliveryHistoryRow,
    OrganizationCustomerIntegrationStatusResponse,
} from './customer-integration.types';

@Component({
    selector: 'app-organization-customer-integration',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        CopyButtonComponent,
        OperationalWarningCardComponent,
        VtePaginationComponent,
    ],
    templateUrl: './organization-customer-integration.component.html',
    styleUrl: './organization-customer-integration.component.css',
})
export class OrganizationCustomerIntegrationComponent implements OnChanges, OnDestroy {
    @Input({required: true}) organizationId!: number | string | null;
    @Input() organizationName: string | null = null;
    /** When embedded under Organization tabs, hide the redundant panel title. */
    @Input() hidePanelHeading = false;

    loading = false;
    saving = false;
    keysLoading = false;
    environment = '';
    integration: CustomerWebhookIntegration | null = null;
    apiKeys: CustomerIntegrationApiKey[] = [];
    orgNameById = new Map<number, string>();

    showCreateModal = false;
    showLinkModal = false;
    showEditModal = false;
    showUnlinkConfirm = false;
    showRawKeyModal = false;
    showGeneratedHmacModal = false;
    showGenerateHmacConfirm = false;
    showRotateHmacConfirm = false;
    showRevokeKeyConfirm = false;
    generatedRawKey = '';
    generatedHmacSecret = '';
    pendingRevokeKey: CustomerIntegrationApiKey | null = null;
    /** UUID for intentional generate; reused only for retries of the same action. */
    pendingRotationRequestId: string | null = null;
    createHmacMode: 'customer' | 'generate' = 'customer';

    checklist: CustomerIntegrationChecklistItem[] = [];
    activity: CustomerIntegrationActivityItem[] = [];
    statusSummary: OrganizationCustomerIntegrationStatusResponse['summary'] | null = null;
    statusLoading = false;

    /** Integration configuration accordion — collapsed by default per Delivery History UX. */
    configurationExpanded = false;

    historyLoading = false;
    historyRows: DeliveryHistoryRow[] = [];
    historyPage = 1;
    historyPageSize = 25;
    historyTotalRecords = 0;
    historyFilters: DeliveryHistoryFilters = {
        outcome: '',
        delivery_status: '',
        event_id: '',
        created_after: '',
        created_before: '',
    };

    linkOptions: CustomerWebhookIntegration[] = [];
    selectedLinkIntegrationId: number | null = null;
    linkLoading = false;

    createForm!: FormGroup;
    editForm!: FormGroup;

    private loadSub: Subscription | null = null;
    private historySub: Subscription | null = null;
    private pendingEditPayload: Partial<{
        name: string;
        webhook_url: string;
        hmac_secret: string;
        is_active: boolean;
    }> | null = null;

    constructor(
        private apiService: ApiService,
        private authService: AuthService,
        private toastr: ToastrService,
        private fb: FormBuilder,
        library: FaIconLibrary,
    ) {
        library.addIcons(
            faSquarePlus,
            faSquareXmark,
            faLink,
            faUnlink,
            faPen,
            faCopy,
            faCheck,
            faKey,
            faShieldHalved,
            faChevronDown,
            faChevronRight,
        );
        this.createForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(64)]],
            webhook_url: ['', [Validators.required, Validators.maxLength(512)]],
            hmac_secret: ['', [Validators.required]],
            is_active: [true],
        });
        this.editForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(64)]],
            webhook_url: ['', [Validators.required, Validators.maxLength(512)]],
            hmac_secret: [''],
            is_active: [true],
        });
    }

    get panelVisible(): boolean {
        return this.authService.isSuperUser() && !!this.resolvedOrganizationId;
    }

    get resolvedOrganizationId(): number | null {
        const raw = this.organizationId;
        if (raw === null || raw === undefined || raw === '') {
            return null;
        }
        const id = Number(raw);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    get sharedHubsWarning(): OperationalWarningView | null {
        const ids = this.integration?.authorized_organization_ids ?? [];
        if (ids.length <= 1) {
            return null;
        }
        return {
            problem: `This integration is shared with ${ids.length} hubs.`,
            impact: 'Changes to the webhook URL, HMAC secret, or API keys apply to all authorized hubs.',
            recommendedAction: 'Confirm the change is intended for every authorized hub before saving.',
            scope: 'organization',
        };
    }

    get authorizedHubRows(): Array<{id: number; name: string; isCurrent: boolean}> {
        const ids = this.integration?.authorized_organization_ids ?? [];
        const currentId = this.resolvedOrganizationId;
        return ids.map((id) => ({
            id,
            name: this.orgNameById.get(id) || `Organization #${id}`,
            isCurrent: currentId === id,
        }));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organizationId'] && this.panelVisible) {
            this.refresh();
        }
    }

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
        this.historySub?.unsubscribe();
        this.clearRawKey();
        this.clearGeneratedHmac();
        this.createForm.reset({name: '', webhook_url: '', hmac_secret: '', is_active: true});
        this.editForm.reset({name: '', webhook_url: '', hmac_secret: '', is_active: true});
    }

    toggleConfiguration(): void {
        this.configurationExpanded = !this.configurationExpanded;
    }

    loadDeliveryHistory(): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId || !this.authService.isSuperUser()) {
            return;
        }
        this.historyLoading = true;
        this.historySub?.unsubscribe();
        this.historySub = this.apiService.getOrganizationCustomerIntegrationDeliveryHistory(orgId, {
            page: this.historyPage,
            page_size: this.historyPageSize,
            outcome: this.historyFilters.outcome || undefined,
            delivery_status: this.historyFilters.delivery_status || undefined,
            event_id: this.historyFilters.event_id.trim() || undefined,
            // YYYY-MM-DD only — backend interprets as UTC calendar day
            // (start inclusive, end exclusive next UTC midnight).
            created_after: this.toUtcDateFilterParam(this.historyFilters.created_after),
            created_before: this.toUtcDateFilterParam(this.historyFilters.created_before),
        }).subscribe({
            next: (response) => {
                this.historyLoading = false;
                this.historyRows = (response.results || []).map((row) => ({
                    ...row,
                    _expanded: false,
                }));
                this.historyPage = response.page || this.historyPage;
                this.historyPageSize = response.page_size || this.historyPageSize;
                this.historyTotalRecords = response.total_records || 0;
            },
            error: (err) => {
                this.historyLoading = false;
                this.historyRows = [];
                this.historyTotalRecords = 0;
                this.handleApiError(err, 'Unable to load delivery history.');
            },
        });
    }

    applyHistoryFilters(): void {
        this.historyPage = 1;
        this.loadDeliveryHistory();
    }

    clearHistoryFilters(): void {
        this.historyFilters = {
            outcome: '',
            delivery_status: '',
            event_id: '',
            created_after: '',
            created_before: '',
        };
        this.historyPage = 1;
        this.loadDeliveryHistory();
    }

    onHistoryPageChange(page: number): void {
        this.historyPage = page;
        this.loadDeliveryHistory();
    }

    onHistoryPageSizeChange(pageSize: number): void {
        this.historyPageSize = pageSize;
        this.historyPage = 1;
        this.loadDeliveryHistory();
    }

    toggleHistoryRow(row: DeliveryHistoryRow): void {
        row._expanded = !row._expanded;
    }

    classificationLabel(classification: string): string {
        switch (classification) {
            case 'intentional_skip':
                return 'Intentionally skipped';
            case 'delivered':
                return 'Delivered';
            case 'delivery_failed':
                return 'Delivery failed';
            case 'delivery_in_progress':
                return 'In progress';
            case 'delivery_only':
                return 'Delivery (legacy)';
            case 'outbox_existing':
                return 'Outbox existing';
            case 'outbox_created_pending_delivery':
                return 'Queued';
            case 'outbox_created':
                return 'Published to outbox';
            default:
                return classification || 'Unknown';
        }
    }

    classificationClass(classification: string): string {
        switch (classification) {
            case 'intentional_skip':
                return 'history-pill--skip';
            case 'delivered':
                return 'history-pill--success';
            case 'delivery_failed':
                return 'history-pill--failed';
            case 'delivery_in_progress':
            case 'outbox_created_pending_delivery':
            case 'outbox_created':
                return 'history-pill--progress';
            default:
                return 'history-pill--neutral';
        }
    }

    deliveryStatusClass(status: string): string {
        switch ((status || '').toUpperCase()) {
            case 'DELIVERED':
                return 'history-pill--success';
            case 'FAILED':
                return 'history-pill--failed';
            case 'RETRYING':
            case 'PENDING':
            case 'PROCESSING':
                return 'history-pill--progress';
            default:
                return 'history-pill--neutral';
        }
    }

    /**
     * Delivery History date filters are UTC calendar days (YYYY-MM-DD).
     * Do not send naive local midnights or fragile 23:59:59 end bounds.
     */
    private toUtcDateFilterParam(dateOnly: string): string | undefined {
        const value = (dateOnly || '').trim();
        if (!value) {
            return undefined;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return undefined;
        }
        return value;
    }

    formatEnvironmentLabel(value: string | null | undefined): string {
        const normalized = (value || '').trim().toLowerCase();
        if (!normalized || normalized === 'unknown') {
            return 'Unknown';
        }
        return normalized.toUpperCase();
    }

    refresh(): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId || !this.authService.isSuperUser()) {
            return;
        }
        this.loading = true;
        this.statusLoading = true;
        this.loadSub?.unsubscribe();
        this.loadSub = forkJoin({
            state: this.apiService.getOrganizationCustomerIntegration(orgId),
            orgs: this.apiService.getOrganizations(1000),
            status: this.apiService.getOrganizationCustomerIntegrationStatus(orgId),
        }).subscribe({
            next: ({state, orgs, status}) => {
                this.loading = false;
                this.statusLoading = false;
                this.environment = state.environment || '';
                this.integration = state.integration;
                this.orgNameById = this.buildOrgNameMap(orgs);
                this.applyStatus(status);
                if (this.integration?.id) {
                    this.loadApiKeys(this.integration.id);
                } else {
                    this.apiKeys = [];
                }
                this.loadDeliveryHistory();
            },
            error: (err) => {
                this.loading = false;
                this.statusLoading = false;
                this.integration = null;
                this.apiKeys = [];
                this.checklist = [];
                this.activity = [];
                this.statusSummary = null;
                this.handleApiError(err, 'Unable to load customer integration.');
                this.loadDeliveryHistory();
            },
        });
    }

    openCreateModal(): void {
        this.createHmacMode = 'customer';
        this.createForm.reset({
            name: '',
            webhook_url: '',
            hmac_secret: '',
            is_active: true,
        });
        this.createForm.get('hmac_secret')?.setValidators([Validators.required]);
        this.createForm.get('hmac_secret')?.updateValueAndValidity();
        this.showCreateModal = true;
    }

    setCreateHmacMode(mode: 'customer' | 'generate'): void {
        this.createHmacMode = mode;
        const control = this.createForm.get('hmac_secret');
        if (mode === 'generate') {
            // Do not send a leftover customer secret with generate_hmac_secret.
            control?.setValue('');
            control?.clearValidators();
        } else {
            control?.setValidators([Validators.required]);
        }
        control?.updateValueAndValidity();
    }

    onHmacSourceKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            this.setCreateHmacMode('generate');
            return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.setCreateHmacMode('customer');
        }
    }

    closeCreateModal(): void {
        this.showCreateModal = false;
        this.saving = false;
        this.createForm.get('hmac_secret')?.setValue('');
    }

    submitCreate(): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId) {
            return;
        }
        if (this.createHmacMode === 'generate') {
            this.createForm.get('hmac_secret')?.setErrors(null);
        }
        if (this.createForm.invalid) {
            validateAllFormFields(this.createForm);
            return;
        }
        const value = this.createForm.getRawValue();
        const payload: {
            name: string;
            webhook_url: string;
            hmac_secret?: string;
            generate_hmac_secret?: boolean;
            is_active?: boolean;
        } = {
            name: String(value.name || '').trim(),
            webhook_url: String(value.webhook_url || '').trim(),
            is_active: !!value.is_active,
        };
        if (this.createHmacMode === 'generate') {
            payload.generate_hmac_secret = true;
        } else {
            payload.hmac_secret = String(value.hmac_secret || '');
        }
        this.saving = true;
        this.apiService.createOrganizationCustomerIntegration(orgId, payload).subscribe({
            next: (response) => {
                this.saving = false;
                this.createForm.get('hmac_secret')?.setValue('');
                this.closeCreateModal();
                this.environment = response.environment || this.environment;
                this.integration = response.integration;
                this.toastr.success('Customer integration created.', 'Success');
                if (response.generated_hmac_secret) {
                    this.generatedHmacSecret = response.generated_hmac_secret;
                    this.showGeneratedHmacModal = true;
                }
                if (this.integration?.id) {
                    this.loadApiKeys(this.integration.id);
                }
                this.refresh();
            },
            error: (err) => {
                this.saving = false;
                this.handleApiError(err, 'Unable to create customer integration.');
            },
        });
    }

    openLinkModal(): void {
        this.selectedLinkIntegrationId = null;
        this.showLinkModal = true;
        this.linkLoading = true;
        this.apiService.listCustomerIntegrations().subscribe({
            next: (response) => {
                this.linkLoading = false;
                this.environment = response.environment || this.environment;
                this.linkOptions = response.results || [];
            },
            error: (err) => {
                this.linkLoading = false;
                this.linkOptions = [];
                this.handleApiError(err, 'Unable to load available integrations.');
            },
        });
    }

    closeLinkModal(): void {
        this.showLinkModal = false;
        this.selectedLinkIntegrationId = null;
        this.linkOptions = [];
        this.saving = false;
    }

    submitLink(): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId || !this.selectedLinkIntegrationId) {
            this.toastr.error('Select an integration to link.', 'Error');
            return;
        }
        this.saving = true;
        this.apiService.linkOrganizationCustomerIntegration(orgId, this.selectedLinkIntegrationId).subscribe({
            next: (response) => {
                this.saving = false;
                this.closeLinkModal();
                this.environment = response.environment || this.environment;
                this.integration = response.integration;
                this.toastr.success('Organization linked to customer integration.', 'Success');
                this.refresh();
            },
            error: (err) => {
                this.saving = false;
                this.handleApiError(err, 'Unable to link customer integration.');
            },
        });
    }

    openEditModal(): void {
        if (!this.integration) {
            return;
        }
        this.editForm.reset({
            name: this.integration.name,
            webhook_url: this.integration.webhook_url,
            hmac_secret: '',
            is_active: this.integration.is_active,
        });
        this.showEditModal = true;
    }

    closeEditModal(): void {
        this.showEditModal = false;
        this.saving = false;
        this.editForm.get('hmac_secret')?.setValue('');
    }

    submitEdit(): void {
        if (!this.integration) {
            return;
        }
        if (this.editForm.invalid) {
            validateAllFormFields(this.editForm);
            return;
        }
        const value = this.editForm.getRawValue();
        const payload: Partial<{
            name: string;
            webhook_url: string;
            hmac_secret: string;
            is_active: boolean;
        }> = {
            name: String(value.name || '').trim(),
            webhook_url: String(value.webhook_url || '').trim(),
            is_active: !!value.is_active,
        };
        const newSecret = String(value.hmac_secret || '');
        if (newSecret.trim()) {
            payload.hmac_secret = newSecret;
            this.pendingEditPayload = payload;
            this.showRotateHmacConfirm = true;
            return;
        }
        this.persistEdit(payload);
    }

    cancelRotateHmac(): void {
        this.showRotateHmacConfirm = false;
        this.pendingEditPayload = null;
    }

    confirmRotateHmac(): void {
        if (!this.pendingEditPayload) {
            return;
        }
        const payload = this.pendingEditPayload;
        this.pendingEditPayload = null;
        this.showRotateHmacConfirm = false;
        this.persistEdit(payload);
    }

    private persistEdit(payload: Partial<{
        name: string;
        webhook_url: string;
        hmac_secret: string;
        is_active: boolean;
    }>): void {
        if (!this.integration) {
            return;
        }
        this.saving = true;
        this.apiService.updateCustomerIntegration(this.integration.id, payload).subscribe({
            next: (updated) => {
                this.saving = false;
                this.editForm.get('hmac_secret')?.setValue('');
                this.closeEditModal();
                this.integration = updated;
                this.toastr.success('Customer integration updated.', 'Success');
                this.refresh();
            },
            error: (err) => {
                this.saving = false;
                this.handleApiError(err, 'Unable to update customer integration.');
            },
        });
    }

    openGenerateHmacConfirm(): void {
        this.pendingRotationRequestId = this.newRotationRequestId();
        this.showGenerateHmacConfirm = true;
    }

    closeGenerateHmacConfirm(): void {
        this.showGenerateHmacConfirm = false;
        // Keep pendingRotationRequestId so an in-flight retry can reuse it;
        // cleared after success or when opening a new intentional rotation.
    }

    confirmGenerateHmac(): void {
        if (!this.integration) {
            return;
        }
        if (!this.pendingRotationRequestId) {
            this.pendingRotationRequestId = this.newRotationRequestId();
        }
        const rotationRequestId = this.pendingRotationRequestId;
        this.saving = true;
        this.apiService.generateCustomerIntegrationHmacSecret(
            this.integration.id,
            rotationRequestId,
        ).subscribe({
            next: (response) => {
                this.saving = false;
                this.showGenerateHmacConfirm = false;
                this.pendingRotationRequestId = null;
                this.integration = response.integration;
                this.generatedHmacSecret = response.generated_hmac_secret || '';
                this.showGeneratedHmacModal = !!this.generatedHmacSecret;
                this.toastr.success('HMAC secret generated. Copy it now — it will not be shown again.', 'Success');
                this.refresh();
            },
            error: (err) => {
                this.saving = false;
                // Preserve rotation_request_id so the operator can retry the same operation.
                this.handleApiError(err, 'Unable to generate HMAC secret.');
            },
        });
    }

    private newRotationRequestId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    closeGeneratedHmacModal(): void {
        this.showGeneratedHmacModal = false;
        this.clearGeneratedHmac();
    }

    openUnlinkConfirm(): void {
        this.showUnlinkConfirm = true;
    }

    closeUnlinkConfirm(): void {
        this.showUnlinkConfirm = false;
        this.saving = false;
    }

    confirmUnlink(): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId || !this.integration) {
            return;
        }
        this.saving = true;
        this.apiService.unlinkOrganizationCustomerIntegration(orgId, this.integration.id).subscribe({
            next: () => {
                this.saving = false;
                this.closeUnlinkConfirm();
                this.integration = null;
                this.apiKeys = [];
                this.toastr.success('Organization unlinked from customer integration.', 'Success');
                this.refresh();
            },
            error: (err) => {
                this.saving = false;
                this.handleApiError(err, 'Unable to unlink customer integration.');
            },
        });
    }

    loadApiKeys(integrationId: number): void {
        this.keysLoading = true;
        this.apiService.listCustomerIntegrationApiKeys(integrationId).subscribe({
            next: (keys) => {
                this.keysLoading = false;
                this.apiKeys = keys || [];
            },
            error: (err) => {
                this.keysLoading = false;
                this.apiKeys = [];
                this.handleApiError(err, 'Unable to load API keys.');
            },
        });
    }

    issueApiKey(): void {
        if (!this.integration) {
            return;
        }
        this.saving = true;
        this.apiService.issueCustomerIntegrationApiKey(this.integration.id, {is_active: true}).subscribe({
            next: (response) => {
                this.saving = false;
                this.generatedRawKey = response?.raw_key ?? '';
                this.showRawKeyModal = !!this.generatedRawKey;
                this.loadApiKeys(this.integration!.id);
                this.toastr.success('API key issued.', 'Success');
            },
            error: (err) => {
                this.saving = false;
                this.handleApiError(err, 'Unable to issue API key.');
            },
        });
    }

    closeRawKeyModal(): void {
        this.showRawKeyModal = false;
        this.clearRawKey();
    }

    toggleApiKey(apiKey: CustomerIntegrationApiKey, event?: Event): void {
        if (!this.integration) {
            return;
        }
        const target = event?.target as HTMLInputElement | undefined;
        const nextStatus = target ? target.checked : !apiKey.is_active;
        // Enabling can proceed immediately; disabling requires confirmation.
        if (nextStatus) {
            this.applyApiKeyActive(apiKey, true);
            return;
        }
        if (target) {
            target.checked = true;
        }
        this.pendingRevokeKey = apiKey;
        this.showRevokeKeyConfirm = true;
    }

    cancelRevokeKey(): void {
        this.showRevokeKeyConfirm = false;
        this.pendingRevokeKey = null;
    }

    confirmRevokeKey(): void {
        if (!this.pendingRevokeKey) {
            return;
        }
        const key = this.pendingRevokeKey;
        this.pendingRevokeKey = null;
        this.showRevokeKeyConfirm = false;
        this.applyApiKeyActive(key, false);
    }

    private applyApiKeyActive(apiKey: CustomerIntegrationApiKey, nextStatus: boolean): void {
        if (!this.integration) {
            return;
        }
        const previous = apiKey.is_active;
        apiKey.is_active = nextStatus;
        apiKey._toggling = true;
        this.apiService.updateCustomerIntegrationApiKey(this.integration.id, apiKey.id, {
            is_active: nextStatus,
        }).subscribe({
            next: (updated) => {
                apiKey._toggling = false;
                apiKey.is_active = updated.is_active;
                this.toastr.success(
                    `API key ${updated.is_active ? 'enabled' : 'disabled'} successfully.`,
                    'Success',
                );
                this.refresh();
            },
            error: (err) => {
                apiKey.is_active = previous;
                apiKey._toggling = false;
                this.handleApiError(err, 'Unable to update API key.');
            },
        });
    }

    checklistStatusClass(status: string): string {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'complete') {
            return 'ok';
        }
        if (normalized === 'failed') {
            return 'down';
        }
        if (normalized === 'not_tested' || normalized === 'unknown') {
            return 'unknown';
        }
        return 'pending';
    }

    checklistStatusLabel(status: string): string {
        const normalized = (status || '').toLowerCase();
        switch (normalized) {
            case 'complete':
                return 'Complete';
            case 'pending':
                return 'Pending';
            case 'failed':
                return 'Failed';
            case 'not_tested':
                return 'Not yet tested';
            case 'unknown':
                return 'Unknown';
            default:
                return status || 'Unknown';
        }
    }

    private applyStatus(status: OrganizationCustomerIntegrationStatusResponse): void {
        this.checklist = status?.checklist || [];
        this.activity = status?.activity || [];
        this.statusSummary = status?.summary || null;
        if (status?.environment) {
            this.environment = status.environment;
        }
    }

    private clearRawKey(): void {
        this.generatedRawKey = '';
    }

    private clearGeneratedHmac(): void {
        this.generatedHmacSecret = '';
    }

    private buildOrgNameMap(response: any): Map<number, string> {
        const map = new Map<number, string>();
        const rows = Array.isArray(response?.results) ? response.results : Array.isArray(response) ? response : [];
        for (const row of rows) {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) {
                continue;
            }
            const name = String(row?.org_name || row?.name || `Organization #${id}`).trim();
            map.set(id, name);
        }
        return map;
    }

    private handleApiError(err: any, fallback: string): void {
        const body = err?.error;
        const code = body?.error_code;
        const detail = typeof body?.detail === 'string'
            ? body.detail
            : typeof body?.detail?.detail === 'string'
                ? body.detail.detail
                : null;

        if (code === 'organization_already_has_current_env_integration') {
            this.toastr.error(
                detail || 'This organization already has a customer integration in the current environment.',
                'Conflict',
            );
            return;
        }
        if (code === 'membership_already_exists') {
            this.toastr.error(detail || 'Organization is already linked to this integration.', 'Conflict');
            return;
        }
        if (code === 'integration_wrong_environment') {
            this.toastr.error(detail || 'Integration belongs to another environment.', 'Forbidden');
            return;
        }
        if (code === 'environment_unresolved' || err?.status === 503) {
            this.toastr.error(
                detail || 'KYRA environment is unresolved. Customer integration management is unavailable.',
                'Unavailable',
            );
            return;
        }
        if (code === 'hmac_rotation_replay_unavailable') {
            this.toastr.error(
                detail
                || 'This rotation already completed and the one-time secret is no longer available. Start a new intentional rotation.',
                'Conflict',
            );
            this.pendingRotationRequestId = null;
            return;
        }
        if (code === 'hmac_rotation_request_stale') {
            this.toastr.error(
                detail
                || 'This rotation request is no longer current. Start a new intentional rotation with Generate New HMAC Secret.',
                'Conflict',
            );
            this.pendingRotationRequestId = null;
            return;
        }

        for (const field of ['name', 'webhook_url', 'hmac_secret', 'integration_id', 'is_active']) {
            const messages = body?.[field];
            if (Array.isArray(messages) && messages.length) {
                messages.forEach((message: string) => this.toastr.error(String(message), 'Error'));
                return;
            }
        }

        if (detail) {
            this.toastr.error(detail, 'Error');
            return;
        }
        if (err?.status !== 403 && err?.status !== 0) {
            this.toastr.error(fallback, 'Error');
        }
    }
}
